import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Collapse, ToggleButton,
  ToggleButtonGroup, Autocomplete, FormControl, InputLabel, Select, MenuItem,
  Pagination, Accordion, AccordionSummary, AccordionDetails, Checkbox,
  Alert, Link, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Avatar from '@mui/material/Avatar';
import { searchHansard, getSearchSuggestion, getNlQuery, getHansardChamberOptions, getHansardSpeakerSuggestions } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import YearRangeSlider from './YearRangeSlider';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import SortSelector, { SORT_OPTIONS, DEFAULT_SORT } from './SortSelector';
import ExportCSVButton from './ExportCSVButton';
import RSSButton from './RSSButton';
import { SaveSearchButton } from './saved-searches';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

const currentYear = new Date().getFullYear();

export default function HansardSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [nlInterpretation, setNlInterpretation] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [sortOrder, setSortOrder] = useState(() => {
    // A sort in the URL (shared link, #143) takes precedence over the saved
    // preference.
    const urlSort = searchParams.get('sort');
    if (urlSort && SORT_OPTIONS.some((o) => o.value === urlSort)) return urlSort;
    try {
      const saved = localStorage.getItem('parlsearch.sort.hansard');
      return SORT_OPTIONS.some((o) => o.value === saved) ? saved : DEFAULT_SORT;
    } catch (e) {
      return DEFAULT_SORT;
    }
  });
  // Restore facet selections from the URL (#143). Single-valued dedicated
  // params map back into the {field:[value]} activeFilters shape; a day in
  // date_from/date_to (same value) restores the day drill-down selection.
  const [activeFilters, setActiveFilters] = useState(() => {
    const f = {};
    // In advanced mode the dedicated params belong to the Advanced form, not
    // the facet checkboxes — restoring them here too would double-apply.
    if (searchParams.get('mode') !== 'advanced') {
      ['chamber', 'chamber_type', 'party', 'speaker_name', 'electorate', 'debate_type', 'parliament'].forEach((k) => {
        const v = searchParams.get(k);
        if (v) f[k] = [v];
      });
    }
    const df = searchParams.get('date_from');
    const dt = searchParams.get('date_to');
    // A same-value date_from/date_to is a day drill-down (facet path).
    if (searchParams.get('mode') !== 'advanced' && df && dt && df === dt) f.date = [df];
    return f;
  });
  const [facets, setFacets] = useState({});

  // Date drill-down state (restored from URL #143)
  const [selectedYear, setSelectedYear] = useState(searchParams.get('date_year') || null);
  const [selectedMonth, setSelectedMonth] = useState(searchParams.get('date_month') || null);

  // Era filter (modern 2011+, historic pre-2011, or all) — restored from URL
  const [selectedEra, setSelectedEra] = useState(searchParams.get('era') || null);

  // Advanced form state. When the URL is an advanced-mode shared link (#143),
  // hydrate the form fields from the dedicated params so reload/share works.
  const isAdvancedUrl = searchParams.get('mode') === 'advanced';
  // #162: map a chamber URL value to the canonical dropdown id. New links send
  // 'house'/'senate'; legacy links (#143) may send a raw variant — normalise
  // those to the canonical id so the dropdown shows the right selection (the
  // backend understands both regardless).
  const normalizeChamberParam = (val) => {
    if (!val) return '';
    const v = val.trim().toLowerCase();
    if (v === 'house' || v === 'senate') return v;
    if (v.includes('rep') || v === 'house' || v.includes('federation')) return 'house';
    if (v.includes('senate')) return 'senate';
    return val;
  };
  const [form, setForm] = useState(() => ({
    keywords: isAdvancedUrl ? (searchParams.get('q') && searchParams.get('q') !== '*' ? searchParams.get('q').replace(/^\((.*)\)$/, '$1') : '') : '',
    // #162: speaker is now keyed on PHID (speaker_id). `speaker` keeps the
    // free-text/name fallback (historic docs without a PHID); `speakerId` is
    // the ID-keyed filter set by picking a typeahead option.
    speaker: isAdvancedUrl ? (searchParams.get('speaker_name') || '') : '',
    speakerId: isAdvancedUrl ? (searchParams.get('speaker_id') || '') : '',
    // #162: chamber is now a canonical id ('house'/'senate') — the backend
    // authority map expands it to every stored spelling. Legacy shared links
    // that sent a raw variant (e.g. 'House of Reps') still work (the backend
    // recognises variants too).
    chamber: isAdvancedUrl ? normalizeChamberParam(searchParams.get('chamber')) : '',
    party: isAdvancedUrl ? (searchParams.get('party') || '') : '',
    electorate: isAdvancedUrl ? (searchParams.get('electorate') || '') : '',
    debateTitle: isAdvancedUrl ? (searchParams.get('debate_type') || '') : ''
  }));
  const [dateYearRange, setDateYearRange] = useState(() => {
    const df = searchParams.get('date_from');
    const dt = searchParams.get('date_to');
    // Only treat date_from/date_to as a slider range in advanced mode and
    // when they are not the same single day (that's a day drill-down).
    if (isAdvancedUrl && df && dt && df !== dt) {
      const y0 = parseInt(df.slice(0, 4), 10);
      const y1 = parseInt(dt.slice(0, 4), 10);
      if (!Number.isNaN(y0) && !Number.isNaN(y1)) return [y0, y1];
    }
    return [2020, currentYear];
  });

  // Available options for autocomplete. Party/electorate still come from the
  // on-mount sample; Chamber + Speaker are now data-backed (#162).
  const [availableOptions, setAvailableOptions] = useState({
    parties: [],
    electorates: []
  });

  // #162: canonical chamber options (House / Senate) from the authority-map
  // endpoint. Falls back to the two canonical labels if the request fails.
  const [chamberOptions, setChamberOptions] = useState([
    { id: 'house', label: 'House of Representatives' },
    { id: 'senate', label: 'Senate' },
  ]);

  // #162: server-backed PHID-grouped speaker typeahead. `speakerOptions` holds
  // [{phid, preferred_name, count}] for the current typed prefix.
  const [speakerOptions, setSpeakerOptions] = useState([]);
  const [speakerInput, setSpeakerInput] = useState(
    isAdvancedUrl ? (searchParams.get('speaker_name') || '') : ''
  );

  // Fetch party/electorate sample options + canonical chamber options on mount.
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchHansard('*', 1, 100);
        if (data.hits) {
          const parties = [...new Set(data.hits.map(h => h.party).filter(Boolean))].sort();
          const electorates = [...new Set(data.hits.map(h => h.electorate).filter(Boolean))].sort();
          setAvailableOptions({ parties, electorates });
        }
      } catch (error) {
        // best-effort sample harvest for party/electorate; ignore failures
      }
    };
    fetchOptions();

    getHansardChamberOptions()
      .then((data) => {
        if (data && Array.isArray(data.options) && data.options.length) {
          setChamberOptions(data.options);
        }
      })
      .catch(() => { /* keep the canonical fallback options */ });
  }, []);

  // #162: debounced PHID-grouped speaker autocomplete. Each keystroke (>=2
  // chars) queries the server, which aggregates by speaker_id so one person is
  // one option (e.g. typing "Gillard" returns a single 83L entry).
  useEffect(() => {
    const term = (speakerInput || '').trim();
    if (term.length < 2) {
      setSpeakerOptions([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      getHansardSpeakerSuggestions(term, 15)
        .then((data) => {
          if (!cancelled && data && Array.isArray(data.suggestions)) {
            setSpeakerOptions(data.suggestions);
          }
        })
        .catch(() => { if (!cancelled) setSpeakerOptions([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [speakerInput]);

  useEffect(() => {
    // Restore the full filtered view from the URL (#143). handleSearch reads
    // the restored activeFilters / selectedYear / selectedMonth / selectedEra
    // / sortOrder / form / dateYearRange via its default args, so a shared or
    // reloaded link re-runs the same filtered search.
    const urlQuery = searchParams.get('q');
    if (searchMode === 'advanced') {
      handleSearch();
    } else if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  // Advanced mode only contributes free-text keywords to the Lucene q.
  // Speaker / Chamber / Party / Electorate / Debate-Title and the date-range
  // slider are sent as dedicated API params instead (see buildFilterParams) —
  // the old `field:"value"` / `date:[…]` clauses never matched the index
  // (#139, #141).
  const buildAdvancedQuery = () => {
    return form.keywords ? `(${form.keywords})` : '*';
  };

  // buildQueryWithFilters previously folded facet selections into the Lucene q,
  // which returned zero (#140, #141, #142). Filtering is now done via dedicated
  // params (buildFilterParams), so the base query is passed through untouched.
  const buildQueryWithFilters = (baseQuery) => baseQuery;

  // Translate the activeFilters facet state + Advanced form + date drill-down
  // into the dedicated, index-aware API params that actually work. The backend
  // params are single-valued, so for a multi-checkbox field we send the first
  // selected value (multi-value OR would require a backend change — out of
  // scope here; deliberately NOT hacked back into q).
  const buildFilterParams = (filters = activeFilters) => {
    const p = {};
    const facetToParam = {
      chamber: 'chamber',
      // #276 — House sub-chamber (main vs Federation Chamber). Only
      // meaningful when chamber=house; UI gates rendering on that.
      chamber_type: 'chamber_type',
      party: 'party',
      speaker_name: 'speaker_name',
      electorate: 'electorate',
      debate_type: 'debate_type',
      parliament: 'parliament',
    };
    Object.entries(filters || {}).forEach(([field, values]) => {
      if (!Array.isArray(values) || values.length === 0) return;
      if (field === 'date') {
        // Day drill-down: single-day range (#142).
        p.date_from = values[0];
        p.date_to = values[0];
        return;
      }
      const param = facetToParam[field];
      if (param && !p[param]) p[param] = values[0];
    });

    // Advanced form fields (only in advanced mode).
    if (searchMode === 'advanced') {
      // #162 Speaker: prefer the ID-keyed filter (speaker_id/PHID) set by
      // picking a typeahead option — it collapses a person's name variants and
      // links by PHID. If the user only typed a free-text name (no option
      // picked, e.g. a historic speaker without a PHID), fall back to the
      // speaker_name string filter so those docs stay findable.
      if (form.speakerId && !p.speaker_id) {
        p.speaker_id = form.speakerId;
      } else if (form.speaker && !p.speaker_name) {
        p.speaker_name = form.speaker;
      }
      // #162 Chamber: the dropdown sends a canonical id ('house'/'senate');
      // the backend authority map expands it to every stored spelling.
      if (form.chamber && !p.chamber) p.chamber = form.chamber;
      if (form.party && !p.party) p.party = form.party;
      if (form.electorate && !p.electorate) p.electorate = form.electorate;
      if (form.debateTitle && !p.debate_type) p.debate_type = form.debateTitle;
      if (dateYearRange[0] > 2020 || dateYearRange[1] < currentYear) {
        if (!p.date_from) p.date_from = `${dateYearRange[0]}-01-01`;
        if (!p.date_to) p.date_to = `${dateYearRange[1]}-12-31`;
      }
    }
    return p;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, year = selectedYear, month = selectedMonth, era = selectedEra, pageSize = perPage, sortBy = sortOrder) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const q = buildQueryWithFilters(baseQuery);
    const filterParams = buildFilterParams(filters);

    setLoading(true);
    setError(null);
    setSuggestion(null);
    setNlInterpretation(null);
    try {
      const data = await searchHansard(q, newPage, pageSize, year, month, era, sortBy, filterParams);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      // Serialize the full filter state into the URL so a filtered view is
      // bookmarkable / shareable and survives reload + pagination (#143).
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      if (sortBy && sortBy !== DEFAULT_SORT) newParams.sort = sortBy;
      if (era) newParams.era = era;
      if (year) newParams.date_year = year;
      if (month) newParams.date_month = month;
      Object.entries(filterParams).forEach(([k, v]) => {
        if (v) newParams[k] = v;
      });
      setSearchParams(newParams);

      // "Did you mean?" — best-effort spelling correction for simple-mode
      // free-text queries only (never blocks or fails the search).
      if (searchMode === 'simple' && baseQuery && baseQuery !== '*') {
        getSearchSuggestion(baseQuery, 'hansard')
          .then((s) => setSuggestion(s && s.suggestion ? s.suggestion : null))
          .catch(() => setSuggestion(null));

        // Natural-language interpretation (rule-based, best-effort). Only
        // surfaced when it understood something AND would change the query.
        getNlQuery(baseQuery)
          .then((r) => {
            if (r && r.interpreted && r.lucene && r.lucene !== baseQuery) {
              setNlInterpretation({ interpreted: r.interpreted, lucene: r.lucene });
            } else {
              setNlInterpretation(null);
            }
          })
          .catch(() => setNlInterpretation(null));
      }
    } catch (err) {
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  // Era change handler
  const handleEraChange = (newEra) => {
    setSelectedEra(newEra);
    // Clear date filters when changing era
    setSelectedYear(null);
    setSelectedMonth(null);
    handleSearch(null, 1, activeFilters, null, null, newEra);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    handleSearch(null, 1, activeFilters, year, null, selectedEra);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    // Extract year from month (e.g., "2024-06" -> "2024")
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month, selectedEra);
  };

  const handleDayClick = (day) => {
    // Filter by the specific day
    const newFilters = { ...activeFilters };
    newFilters['date'] = [day];
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters, selectedYear, selectedMonth, selectedEra);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    // Also remove any date filter
    const newFilters = { ...activeFilters };
    delete newFilters['date'];
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters, null, null, selectedEra);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters, selectedYear, selectedMonth, selectedEra);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {}, selectedYear, selectedMonth, selectedEra);
  };

  const facetToField = (facetName) => {
    const mapping = {
      'chambers': 'chamber',
      'chamber_types': 'chamber_type',
      'parties': 'party',
      'speakers': 'speaker_name',
      'electorates': 'electorate',
      'parliaments': 'parliament',
      'years': 'date',
      'debate_types': 'debate_type'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'chambers': 'Chamber',
      'chamber_types': 'House sub-chamber',
      'parties': 'Party',
      'speakers': 'Speaker',
      'electorates': 'Electorate',
      'parliaments': 'Parliament',
      'years': 'Year',
      'months': 'Month',
      'days': 'Day',
      'debate_types': 'Debate Type'
    };
    return names[facetName] || facetName;
  };

  // Short explanations of each filter, shown as tooltips on the facet
  // accordion headers (#88).
  const facetDescription = (facetName) => {
    const descriptions = {
      'chambers': 'Limit results to the House of Representatives or the Senate.',
      'chamber_types': 'Within the House, split main chamber from the Federation Chamber (called the Main Committee 1994-2012).',
      'parties': "Filter by the speaker's political party.",
      'speakers': 'Filter by the member or senator who spoke.',
      'electorates': "Filter by the speaker's electorate.",
      'parliaments': 'Filter by which Parliament the debate occurred in.',
      'years': 'Drill down to a specific year.',
      'months': 'Drill down to a specific month.',
      'days': 'Drill down to a specific sitting day.',
      'debate_types': 'Filter by the type of debate or proceeding.'
    };
    return descriptions[facetName] || '';
  };

  // Format month for display (e.g., "2024-06" -> "June 2024")
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  };

  // Format day for display (e.g., "2024-06-15" -> "15 June 2024")
  const formatDay = (dayStr) => {
    const date = new Date(dayStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Get short month name (e.g., "2024-06" -> "Jun")
  const getShortMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-AU', { month: 'short' });
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    // Reset to page 1 and re-search with new page size
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, selectedEra, newSize);
  };

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    try {
      localStorage.setItem('parlsearch.sort.hansard', newSort);
    } catch (e) {
      // localStorage unavailable (e.g. private mode) — sort still applies for
      // this session, just not persisted.
    }
    // Reset to page 1 and re-search with the new sort order
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, selectedEra, perPage, newSort);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      speaker: '',
      speakerId: '',
      chamber: '',
      party: '',
      electorate: '',
      debateTitle: ''
    });
    setSpeakerInput('');
    setSpeakerOptions([]);
    setDateYearRange([2020, currentYear]);
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedEra(null);
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Hansard (Parliamentary Debates)</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* #259 — Era filter moved to left-rail "Records" facet (primary,
          expanded by default). The top-row ToggleButtonGroup was removed. */}

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip
              title='Use quotes for an exact phrase ("climate change"), AND / OR / NOT to combine terms, and * as a wildcard. Leave blank to browse everything.'
              placement="top-start"
              arrow
              enterDelay={500}
            >
              <TextField
                fullWidth
                placeholder="Search parliamentary debates..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Tooltip>
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search in speech content"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              {/* #162: PHID-grouped, server-backed speaker typeahead. Picking
                  an option filters by speaker_id (one entry per person, all
                  their name variants); a free-typed name (no option chosen)
                  still works via the speaker_name fallback for historic docs
                  that lack a PHID (~34%). */}
              <Autocomplete
                fullWidth
                size="small"
                freeSolo
                options={speakerOptions}
                filterOptions={(opts) => opts}
                getOptionLabel={(opt) =>
                  typeof opt === 'string' ? opt : (opt.preferred_name || '')
                }
                isOptionEqualToValue={(opt, val) =>
                  (opt && opt.phid) === (val && val.phid)
                }
                inputValue={speakerInput}
                onInputChange={(event, newInput, reason) => {
                  setSpeakerInput(newInput);
                  // Typing replaces a previously-picked PHID with a free-text
                  // name; clearing the field clears both.
                  if (reason === 'input' || reason === 'clear') {
                    setForm((f) => ({ ...f, speakerId: '', speaker: newInput }));
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'object' && newValue.phid) {
                    setForm((f) => ({
                      ...f,
                      speakerId: newValue.phid,
                      speaker: newValue.preferred_name || '',
                    }));
                    setSpeakerInput(newValue.preferred_name || '');
                  } else {
                    const str = typeof newValue === 'string' ? newValue : '';
                    setForm((f) => ({ ...f, speakerId: '', speaker: str }));
                    setSpeakerInput(str);
                  }
                }}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.phid}>
                    {opt.preferred_name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({opt.phid} · {opt.count.toLocaleString()})
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Speaker"
                    helperText={
                      form.speakerId
                        ? `Filtering by PHID ${form.speakerId}`
                        : 'Type a name; pick a result to filter by parliamentarian (PHID). Pre-PHID historic speakers match by name.'
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Chamber</InputLabel>
                {/* #162: canonical options from the authority map (House /
                    Senate); the backend expands the chosen id to every stored
                    spelling. */}
                <Select value={form.chamber} onChange={handleChange('chamber')} label="Chamber">
                  <MenuItem value="">All Chambers</MenuItem>
                  {chamberOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.parties}
                value={form.party}
                onChange={handleAutocompleteChange('party')}
                renderInput={(params) => <TextField {...params} label="Party" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.electorates}
                value={form.electorate}
                onChange={handleAutocompleteChange('electorate')}
                renderInput={(params) => <TextField {...params} label="Electorate" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Debate Title"
                value={form.debateTitle}
                onChange={handleChange('debateTitle')}
                placeholder="e.g., COMMITTEES, QUESTIONS"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <YearRangeSlider
                label="Date Range"
                value={dateYearRange}
                onChange={setDateYearRange}
                min={2020}
                max={currentYear}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Loading skeleton for initial load */}
      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {/* Error state */}
      {!initialLoading && error && (
        <ErrorState
          message={error}
          onRetry={() => handleSearch()}
        />
      )}

      {/* "Did you mean?" spelling suggestion */}
      {!initialLoading && !error && !loading && results && suggestion && (
        <Alert
          severity="info"
          icon={<SearchIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Did you mean:{' '}
          <Link
            component="button"
            type="button"
            underline="hover"
            sx={{ fontWeight: 600, fontStyle: 'italic', verticalAlign: 'baseline' }}
            onClick={() => {
              setQuery(suggestion);
              setSuggestion(null);
              handleSearch(suggestion);
            }}
          >
            {suggestion}
          </Link>
          ?
        </Alert>
      )}

      {/* Natural-language interpretation (rule-based, #95) */}
      {!initialLoading && !error && !loading && results && nlInterpretation && (
        <Alert
          severity="info"
          icon={<TuneIcon fontSize="inherit" />}
          onClose={() => setNlInterpretation(null)}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Interpreted as: {nlInterpretation.interpreted}.{' '}
          <Link
            component="button"
            type="button"
            underline="hover"
            sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
            onClick={() => {
              const lq = nlInterpretation.lucene;
              setQuery(lq);
              setNlInterpretation(null);
              handleSearch(lq);
            }}
          >
            Search this instead
          </Link>
        </Alert>
      )}

      {/* Empty state when no results */}
      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="hansard"
          onSuggestionClick={(q) => {
            setQuery(q);
            handleSearch(q);
          }}
          onReset={handleReset}
        />
      )}

      {/* Results display */}
      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {/* #259 — Records (Modern / Historic) — primary facet,
                    expanded by default. Two checkboxes; both unchecked OR
                    both checked → era= not sent → backend dual-indexes
                    hansard + hansard_historic. */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Tooltip
                      title="Modern Hansard (2011+) and Historic Hansard (1901-2010). Leave both unchecked or both checked to search everything."
                      placement="top-start"
                      arrow
                      enterDelay={400}
                    >
                      <Typography variant="subtitle2" sx={{ display: 'inline-block' }}>
                        Records
                      </Typography>
                    </Tooltip>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <List dense sx={{ py: 0 }}>
                      <ListItem disablePadding>
                        <Checkbox
                          size="small"
                          checked={selectedEra === 'modern'}
                          onChange={() => {
                            // toggle Modern. Pairs: none/both = null era.
                            const isModern = selectedEra === 'modern';
                            const isHistoric = selectedEra === 'historic';
                            let next;
                            if (isModern) next = null; // was just Modern → clear
                            else if (isHistoric) next = null; // was Historic → both = null
                            else next = 'modern';
                            handleEraChange(next);
                          }}
                          inputProps={{ 'aria-label': 'Filter by Records: Modern (2011+)' }}
                        />
                        <ListItemText
                          primary="Modern (2011+)"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                      <ListItem disablePadding>
                        <Checkbox
                          size="small"
                          checked={selectedEra === 'historic'}
                          onChange={() => {
                            const isModern = selectedEra === 'modern';
                            const isHistoric = selectedEra === 'historic';
                            let next;
                            if (isHistoric) next = null; // was just Historic → clear
                            else if (isModern) next = null; // was Modern → both = null
                            else next = 'historic';
                            handleEraChange(next);
                          }}
                          inputProps={{ 'aria-label': 'Filter by Records: Historic (1901-2010)' }}
                        />
                        <ListItemText
                          primary="Historic (1901-2010)"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>

                {/* Date Hierarchy Filter */}
                {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={activeFilters['date']?.[0] || null}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                    themeColor="primary"
                  />
                )}

                {/* Other facets. #276 — chamber_types (House sub-chamber)
                    sits next to chambers; we only render it when the active
                    chamber selection is House or no chamber is set, since
                    Senate has no Federation Chamber. */}
                {sortFacetsBooleansFirst(['chambers', 'chamber_types', 'parliaments', 'parties', 'speakers', 'electorates', 'debate_types'], facets).map((facetName) => {
                  if (!facets[facetName] || facets[facetName].length === 0) return null;
                  // #276 — hide the House sub-chamber facet when the user has
                  // explicitly scoped to Senate (it has no FC).
                  if (facetName === 'chamber_types') {
                    const chamberSel = activeFilters['chamber'] || [];
                    const onlySenate = chamberSel.length > 0 && chamberSel.every((c) => /senate/i.test(String(c)));
                    if (onlySenate) return null;
                  }
                  // #276 — friendly labels for the chamber_types buckets.
                  const chamberTypeLabel = (val) => {
                    if (val === 'federation_chamber') return 'Federation Chamber';
                    if (val === 'house_main') return 'House (main chamber)';
                    return val;
                  };
                  return (
                    <Accordion key={facetName} defaultExpanded={['chambers', 'chamber_types', 'parliaments'].includes(facetName)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Tooltip
                          title={facetDescription(facetName)}
                          placement="top-start"
                          arrow
                          enterDelay={400}
                          disableHoverListener={!facetDescription(facetName)}
                          disableFocusListener={!facetDescription(facetName)}
                          disableTouchListener={!facetDescription(facetName)}
                        >
                          <Typography variant="subtitle2" sx={{ display: 'inline-block' }}>
                            {facetDisplayName(facetName)}
                          </Typography>
                        </Tooltip>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facets[facetName].slice(0, 15).map((facet) => {
                            const isChamberType = facetName === 'chamber_types';
                            const label = isChamberType ? chamberTypeLabel(facet.value) : (facetName === 'parliaments' ? `Parliament ${facet.value}` : facet.value);
                            return (
                              <ListItem key={facet.value} disablePadding>
                                <Checkbox
                                  size="small"
                                  checked={activeFilters[facetToField(facetName)]?.includes(String(facet.value)) || false}
                                  onChange={() => handleFacetClick(facetToField(facetName), String(facet.value))}
                                  inputProps={{
                                    'aria-label': `Filter by ${facetDisplayName(facetName)}: ${label}`
                                  }}
                                />
                                <ListItemText
                                  primary={`${label} (${facet.count.toLocaleString()})`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              {/* #259 — Std results layout. Row 1: count (left) + pagination (right). */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">
                  {results.total.toLocaleString()} speeches found
                </Typography>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              {/* Row 2: Per page · Sort · Export · Save · RSS */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <SortSelector value={sortOrder} onChange={handleSortChange} />
                <ExportCSVButton
                  dataType="hansard"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <SaveSearchButton
                  searchType="hansard"
                  queryText={query || '*'}
                  parameters={{ page, per_page: perPage, ...activeFilters }}
                  resultCount={results.total}
                />
                <RSSButton collection="hansard" query={query || '*'} />
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box>
                            {/* Speech title as the main link */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                              <Typography
                                component={RouterLink}
                                to={`/hansard/${hit.id}`}
                                variant="resultTitle"
                                sx={{
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {hit.debate_title}
                                {hit.subdebate_title && ` - ${hit.subdebate_title}`}
                              </Typography>
                              <Chip
                                label={hit.chamber}
                                size="small"
                                color={hit.chamber === 'Senate' ? 'error' : 'primary'}
                              />
                              {/* #276 — Federation Chamber discriminator.
                                  Modern docs: is_federation_chamber bool.
                                  Historic docs (pre-2012): debate_title
                                  contains "MAIN COMMITTEE" (the chamber's
                                  pre-2012 name). */}
                              {(hit.is_federation_chamber === true || /MAIN COMMITTEE/i.test(hit.debate_title || '')) && (
                                <Tooltip title="Federation Chamber (the House's second chamber; called the Main Committee 1994-2012)" arrow placement="top">
                                  <Chip
                                    label="FC"
                                    size="small"
                                    variant="outlined"
                                    sx={{ color: '#1565c0', borderColor: '#1565c0', fontWeight: 600 }}
                                  />
                                </Tooltip>
                              )}
                              {hit.era === 'historic' && (
                                <Chip
                                  label="Historic"
                                  size="small"
                                  variant="outlined"
                                  sx={{ color: '#8b4513', borderColor: '#8b4513' }}
                                />
                              )}
                              {hit.fragment_type === 'procedural' && (
                                <Chip label="Procedural" size="small" variant="outlined" color="default" />
                              )}
                            </Box>

                            {/* Speaker info below the title */}
                            {hit.speaker_name && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                <Avatar
                                  src={hit.speaker_id ? getParliamentarianPhotoUrl(hit.speaker_id) : null}
                                  alt={hit.speaker_name || 'Speaker'}
                                  sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}
                                >
                                  {hit.speaker_name?.[0]}
                                </Avatar>
                                {hit.speaker_id ? (
                                  <Typography
                                    component={RouterLink}
                                    to={`/parliamentarians/${hit.speaker_id}`}
                                    variant="body1"
                                    sx={{
                                      color: 'text.primary',
                                      textDecoration: 'none',
                                      fontWeight: 500,
                                      '&:hover': { textDecoration: 'underline', color: 'ledger.accentText' }
                                    }}
                                  >
                                    {hit.speaker_name}
                                  </Typography>
                                ) : (
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {hit.speaker_name}
                                  </Typography>
                                )}
                                {hit.party && (
                                  <Chip label={hit.party} size="small" variant="outlined" />
                                )}
                                {hit.electorate && (
                                  <Typography variant="body2" color="text.secondary">
                                    ({hit.electorate})
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {[
                                formatDate(hit.date),
                                hit.parliament && `Parliament ${hit.parliament}${hit.session ? `, Session ${hit.session}` : ''}`,
                                hit.page && `Page ${hit.page}`
                              ].filter(Boolean).join(' · ')}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
